export interface BotIdentity {
  username: string;
  displayName: string;
  equippedSkin: string;
  level: number;
  userId: string;
}

export interface BotIdentityFactoryOptions {
  random?: () => number;
  nowMs?: number;
  attemptId?: string;
}

const FIRST_NAMES = [
  "Milo", "Nora", "Jules", "Rhea", "Damon", "Lena", "Owen", "Piper",
  "Romy", "Tess", "Felix", "Soren", "Willa", "Iris", "Caleb", "Vera"
] as const;

const LAST_NAMES = [
  "Quinn", "Mercer", "Stone", "Vale", "Harper", "Rowe", "Sterling", "Knox",
  "Brooks", "Keller", "Monroe", "Bennett"
] as const;

export const BOT_SKIN_POOL = [
  "skin_08118", "skin_08119", "skin_08120", "skin_08121", "skin_08130",
  "skin_08140", "skin_08150", "skin_08200", "skin_08250", "skin_08300",
  "skin_08400", "skin_08500", "skin_08600", "skin_08700", "skin_08800",
  "skin_09000", "skin_09100", "skin_09200", "skin_09500", "skin_09800",
  "skin_10000", "skin_10500", "skin_11000", "skin_11500", "skin_12000",
  "skin_12500", "skin_13000"
] as const;

const DISCLOSURE_TOKEN_PATTERN = /(^|[^a-z0-9])(bot|ai|npc)([^a-z0-9]|$)/i;

function pick<T>(items: readonly T[], random: () => number): T {
  const idx = Math.floor(random() * items.length);
  return items[Math.max(0, Math.min(items.length - 1, idx))];
}

function randomInt(min: number, max: number, random: () => number): number {
  const span = max - min + 1;
  return min + Math.floor(random() * span);
}

export function hasDisclosureToken(text: string): boolean {
  return DISCLOSURE_TOKEN_PATTERN.test(text.toLowerCase());
}

export function createBotIdentity(options: BotIdentityFactoryOptions = {}): BotIdentity {
  const random = options.random ?? Math.random;
  const nowMs = options.nowMs ?? Date.now();
  const attemptId = options.attemptId ?? `${nowMs}-${Math.floor(random() * 1_000_000).toString(36)}`;

  const first = pick(FIRST_NAMES, random);
  const last = pick(LAST_NAMES, random);
  const usernameSuffix = String(randomInt(10, 99, random));

  const username = `${first.toLowerCase()}_${last.toLowerCase()}${usernameSuffix}`;
  const displayName = `${first} ${last}`;
  const equippedSkin = pick(BOT_SKIN_POOL, random);
  const level = randomInt(4, 32, random);
  const userId = `fallback-${attemptId}-${Math.floor(random() * 1_000_000).toString(36)}`;

  if (hasDisclosureToken(username) || hasDisclosureToken(displayName)) {
    return createBotIdentity({ random, nowMs, attemptId: `${attemptId}-r` });
  }

  return Object.freeze({ username, displayName, equippedSkin, level, userId });
}
